import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Loader2, Shield, X, XCircle } from "lucide-react";
import { verifyCoinToss, truncateHash } from "../../utils/coinFairHelpers";
import { sideLabel } from "../../utils/coinFlipHelpers";
import { COIN_SIDE_LABEL } from "../../utils/coinPoolHelpers";
import { faceLabel } from "../../utils/bondDiceHelpers";

const BondCoinFairPanel = ({ round, game = "pool", compact = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [seedInput, setSeedInput] = useState("");

  const roundId = round?.id || round?._id;
  const seedHash = round?.seedHash;
  const revealedSeed = round?.serverSeed;

  const runVerify = useCallback(
    async (seed) => {
      if (!roundId || !seed) return;
      setLoading(true);
      try {
        const data = await verifyCoinToss({ game, roundId, serverSeed: seed.trim() });
        setResult(data);
      } catch {
        setResult({ valid: false, error: true });
      } finally {
        setLoading(false);
      }
    },
    [game, roundId]
  );

  useEffect(() => {
    setResult(null);
    setSeedInput(revealedSeed || "");
  }, [roundId, revealedSeed]);

  useEffect(() => {
    if (!seedHash || !revealedSeed || !roundId) return undefined;
    runVerify(revealedSeed);
    return undefined;
  }, [seedHash, revealedSeed, roundId, runVerify]);

  if (!seedHash) return null;

  const sideName = (side) => {
    if (game === "flip") return sideLabel(side);
    if (game === "dice") return `Face ${faceLabel(side)}`;
    return COIN_SIDE_LABEL[side] || side;
  };

  const pill = (
    <button
      type="button"
      className={`bcoin-meta-pill bcoin-meta-pill-fair${compact ? " bcoin-meta-pill-compact" : ""}`}
      onClick={() => setOpen(true)}
      title={seedHash}
    >
      <Shield size={13} />
      <span>{revealedSeed ? (game === "dice" ? "Verify roll" : "Verify toss") : "Provably fair locked"}</span>
    </button>
  );

  const modal = open
    ? createPortal(
        <div className="bcoin-fair-root" role="dialog" aria-modal="true">
          <button type="button" className="bcoin-fair-backdrop" aria-label="Close" onClick={() => setOpen(false)} />
          <div className="bcoin-fair-sheet">
            <header className="bcoin-fair-head">
              <div>
                <p className="bcoin-fair-kicker">Provably fair</p>
                <h2>{game === "dice" ? "Verify dice roll" : "Verify coin toss"}</h2>
              </div>
              <button type="button" className="bcoin-fair-close" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </header>

            <dl className="bcoin-fair-fields">
              <div>
                <dt>Round</dt>
                <dd className="bcoin-fair-mono">{truncateHash(String(roundId), 10, 8)}</dd>
              </div>
              <div>
                <dt>Seed hash (pre-toss)</dt>
                <dd className="bcoin-fair-mono">{seedHash}</dd>
              </div>
              {revealedSeed ? (
                <div>
                  <dt>Server seed (revealed)</dt>
                  <dd className="bcoin-fair-mono">{revealedSeed}</dd>
                </div>
              ) : (
                <div>
                  <dt>Server seed</dt>
                  <dd>
                    <input
                      className="bcoin-fair-input"
                      value={seedInput}
                      onChange={(e) => setSeedInput(e.target.value)}
                      placeholder="Paste seed after reveal"
                    />
                  </dd>
                </div>
              )}
            </dl>

            {!revealedSeed ? (
              <button
                type="button"
                className="bcoin-btn bcoin-btn-primary"
                disabled={loading || !seedInput.trim()}
                onClick={() => runVerify(seedInput)}
              >
                {loading ? <Loader2 size={14} className="bcoin-spin" /> : "Verify"}
              </button>
            ) : null}

            {result ? (
              <div className={`bcoin-fair-result ${result.valid ? "ok" : "bad"}`}>
                {result.valid ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                <div>
                  {result.valid ? (
                    <>
                      <strong>Verified</strong>
                      <p>
                        Derived outcome: {sideName(result.derivedOutcome)}
                        {result.matchesRecorded === true ? " · matches recorded result" : null}
                        {result.matchesRecorded === false ? " · mismatch with recorded result" : null}
                      </p>
                    </>
                  ) : (
                    <>
                      <strong>Not verified</strong>
                      <p>Seed does not match the published hash.</p>
                    </>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {pill}
      {modal}
    </>
  );
};

export default BondCoinFairPanel;
